'use client';

import React, { useLayoutEffect, useRef, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { OverlappingCards } from "../ui";
import CTAButton from '../ui/CTAButton';
import { trackEvent } from '@/lib/analytics';
import '../../app/styles/Team.scss';

gsap.registerPlugin(ScrollTrigger);

// Container component for list view with staggered animation
function ListContainer({ children }: { children: React.ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      // If reduced motion, immediately add animate class
      containerRef.current.classList.add('animate');
    } else {
      // Trigger animation on mount with delay
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          containerRef.current?.classList.add('animate');
        });
      });
    }
  }, []);
  
  return (
    <div ref={containerRef} className="animate-stagger-items relative">
      {children}
    </div>
  );
}

interface CardSectionItem {
  id: string | number;
  imageSrc: string;
  alt: string;
  title?: string;
  subtitle?: string;
  extraLine?: string;
}

interface CardSectionProps {
  items: CardSectionItem[];
  title?: string;
  body?: string;
  defaultTitle?: string;
  defaultBody?: string;
  className?: string;
  
  // Card styling
  hoverBackgroundColor?: string;
  cardWidth?: number;
  cardHeight?: number;
  overlapPx?: number;
  radiusPx?: number;
  
  // CTAs
  ctaOneText?: string;
  ctaOneLink?: string;
  ctaTwoText?: string;
  ctaTwoLink?: string;
  
  // View toggle (for About page team section)
  showViewToggle?: boolean;
  onViewChange?: (view: 'cards' | 'list') => void;
  
  // List view (for About page team section)
  renderListItem?: (item: CardSectionItem, index: number) => React.ReactNode;
  
  // Show/hide title field
  showTitle?: boolean;
  
  // Body text styling
  bodyClassName?: string;
  
  // Tooltip control
  showTooltip?: boolean;
  
  // Maximum cards per row
  maxPerRow?: number;
}

export default function CardSection({
  items,
  title,
  body,
  defaultTitle = 'Who we are',
  defaultBody = 'Independent minds, collective impact.',
  className = '',
  hoverBackgroundColor,
  cardWidth = 280,
  cardHeight = 340,
  overlapPx = 24,
  radiusPx = 12,
  ctaOneText,
  ctaOneLink,
  ctaTwoText,
  ctaTwoLink,
  showViewToggle = false,
  onViewChange,
  renderListItem,
  showTitle = true,
  bodyClassName = 'body-xl-display',
  showTooltip = true,
  maxPerRow = 5,
}: CardSectionProps) {
  const pathname = usePathname();
  const [view, setView] = React.useState<'cards' | 'list'>('cards');
  const collectiveRef = useRef<HTMLSpanElement>(null);
  const collectiveUnderlineRef = useRef<SVGPathElement>(null);

  const handleViewChange = (newView: 'cards' | 'list') => {
    setView(newView);
    onViewChange?.(newView);
  };

  // Animate underline for "collective" when it scrolls into view
  useLayoutEffect(() => {
    if (!collectiveUnderlineRef.current) return;

    const path = collectiveUnderlineRef.current;
    const pathLength = path.getTotalLength();

    // Set up the initial state
    gsap.set(path, {
      strokeDasharray: pathLength,
      strokeDashoffset: pathLength,
    });

    const context = gsap.context(() => {
      gsap.to(path, {
        strokeDashoffset: 0,
        duration: 1.2,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: collectiveRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });
    });

    return () => context.revert();
  }, []);

  // Helper function to render body text with underline on "collective"
  const renderBodyWithUnderline = (text: string) => {
    const parts = text.split('collective');
    if (parts.length === 2) {
      return (
        <>
          {parts[0]}
          <span ref={collectiveRef} className="relative inline-block">
            collective
            <svg
              className="absolute -bottom-1 left-0 w-full h-auto max-w-[257px]"
              width="257"
              height="10"
              viewBox="0 0 257 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                ref={collectiveUnderlineRef}
                d="M1.25028 7.95115C3.13715 7.53529 5.02401 7.11943 44.7006 5.80187C84.3772 4.48431 161.786 2.27765 203.128 1.35626C247.4 0.8656 253.199 2.20352 255.699 2.75177"
                stroke="#E60000"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </span>
          {parts[1]}
        </>
      );
    }
    return text;
  };

  return (
    <div className={`card-section global-padding ${className}`}>
      <div className="team-wrap">
        <div className="lg:mb-[80px] mb-[40px]">
          {showTitle && (
            <div className="flex flex-row items-center justify-between gap-3 mb-4">
              <h2 className="heading-2">
                {title || defaultTitle}
              </h2>
              {showViewToggle && (
                <div className="inline-flex items-center gap-2" role="group" aria-label="View options">
                  <button
                    type="button"
                    className="p-0 m-0 bg-transparent border-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
                    aria-pressed={view === 'cards'}
                    aria-label="Card view"
                    onClick={() => handleViewChange('cards')}
                  >
                    <span className="inline-flex items-center justify-center" aria-hidden="true">
                      <Image
                        src={view === 'cards' ? '/images/card-icon-selected.png' : '/images/card-icon-default.png'}
                        alt=""
                        width={40}
                        height={40}
                      />
                    </span>
                  </button>
                  <button
                    type="button"
                    className="p-0 m-0 bg-transparent border-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
                    aria-pressed={view === 'list'}
                    aria-label="List view"
                    onClick={() => handleViewChange('list')}
                  >
                    <span className="inline-flex items-center justify-center" aria-hidden="true">
                      <Image
                        src={view === 'list' ? '/images/list-icon-selected.png' : '/images/list-icon-default.png'}
                        alt=""
                        width={40}
                        height={40}
                      />
                    </span>
                  </button>
                </div>
              )}
            </div>
          )}
          
          <div className="flex flex-row items-center justify-between gap-3">
            <p className={bodyClassName}>
              {renderBodyWithUnderline(body || defaultBody)}
            </p>
            {!showTitle && showViewToggle && (
              <div className="inline-flex items-center gap-2" role="group" aria-label="View options">
                <button
                  type="button"
                  className="p-0 m-0 bg-transparent border-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
                  aria-pressed={view === 'cards'}
                  aria-label="Card view"
                  onClick={() => handleViewChange('cards')}
                >
                  <span className="inline-flex items-center justify-center" aria-hidden="true">
                    <Image
                      src={view === 'cards' ? '/images/card-icon-selected.png' : '/images/card-icon-default.png'}
                      alt=""
                      width={40}
                      height={40}
                    />
                  </span>
                </button>
                <button
                  type="button"
                  className="p-0 m-0 bg-transparent border-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
                  aria-pressed={view === 'list'}
                  aria-label="List view"
                  onClick={() => handleViewChange('list')}
                >
                  <span className="inline-flex items-center justify-center" aria-hidden="true">
                    <Image
                      src={view === 'list' ? '/images/list-icon-selected.png' : '/images/list-icon-default.png'}
                      alt=""
                      width={40}
                      height={40}
                    />
                  </span>
                </button>
              </div>
            )}
          </div>
          {showViewToggle && (
            <hr className="mt-[6px] lg:mt-[16px] border-black border-t-2" />
          )}
        </div>

        {view === 'cards' ? (
          <OverlappingCards
            className="mt-4"
            items={items}
            hoverBackgroundColor={hoverBackgroundColor}
            cardWidth={cardWidth}
            cardHeight={cardHeight}
            overlapPx={overlapPx}
            radiusPx={radiusPx}
            showTooltip={showTooltip}
            maxPerRow={maxPerRow}
          />
        ) : renderListItem ? (
          <div className="w-full max-w-8xl mx-auto -mt-[9px] lg:-mt-[37px]">
            <ListContainer>
              {items.map((item, index) => (
                <div key={index} className="stagger-item">
                  {renderListItem(item, index)}
                </div>
              ))}
            </ListContainer>
          </div>
        ) : null}

        {/* CTA Buttons */}
        {(ctaOneText || ctaTwoText) && (
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-[24px] md:mt-[90px]">
            {ctaOneText && ctaOneLink && (
              <CTAButton 
                href={ctaOneLink} 
                className="bg-[#FDFEF6]"
                onClick={() => {
                  trackEvent('hp_click', {
                    click_text: ctaOneText,
                    click_url: ctaOneLink,
                    page_path: pathname
                  });
                }}
              >
                {ctaOneText}
              </CTAButton>
            )}
            {ctaTwoText && ctaTwoLink && (
              <CTAButton 
                href={ctaTwoLink} 
                className="bg-[#FDFEF6]"
                onClick={() => {
                  trackEvent('hp_click', {
                    click_text: ctaTwoText,
                    click_url: ctaTwoLink,
                    page_path: pathname
                  });
                }}
              >
                {ctaTwoText}
              </CTAButton>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

